"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { HScroll } from "@/components/ui/HScroll";
import { cn } from "@/lib/cn";
import { compact } from "@/lib/format";
import type { Creator } from "@/lib/onlytraffic";
import { useAds } from "./Ads";
import { useUi } from "@/components/ui/use-ui";

// OnlyFans creators from OnlyTraffic (lib/onlytraffic.ts): a row of portrait cards, marked as sponsored,
// in the video feed and under a video. Asked for only once the site knows the visitor has no Premium.
// A card links to our /go, which counts the click and opens the creator's page in a new tab; a card
// that has been half on screen for a second is reported as seen (OnlyTraffic pays for clicks, and uses
// what was seen to rotate creators).

/** The visible share and time that make a card "seen" (the IAB's display standard, as OnlyTraffic asks). */
const SEEN_SHARE = 0.5;
const SEEN_MS = 1000;

export function CreatorsRow({ count = 8, className }: { count?: number; className?: string }) {
  const { creators: on } = useAds();
  const t = useUi();
  const pathname = usePathname();
  const [found, setFound] = useState<{ page: string; list: Creator[] } | null>(null);

  useEffect(() => {
    if (!on) return;
    const page = window.location.pathname + window.location.search;
    // The page's title too: OnlyTraffic picks creators that suit it.
    const title = document.title.slice(0, 200);
    const ctrl = new AbortController();
    fetch(`/api/creators?n=${count}&p=${encodeURIComponent(page)}&t=${encodeURIComponent(title)}`, { cache: "no-store", signal: ctrl.signal })
      .then((r) => (r.ok ? (r.json() as Promise<{ creators?: Creator[] }>) : null))
      .then((d) => setFound({ page, list: d?.creators ?? [] }))
      .catch(() => {});
    return () => ctrl.abort();
  }, [on, pathname, count]);

  if (!on || !found?.list.length) return null;
  return (
    <section aria-label={t.video.onlyfansCreators} className={cn("min-w-0", className)}>
      <div className="mb-2.5 flex items-baseline gap-2.5">
        <h2 className="text-[15px] text-fg">{t.video.onlyfansCreators}</h2>
        <span className="text-[11px] uppercase tracking-[0.12em] text-faint">{t.video.sponsored}</span>
      </div>
      <HScroll className="gap-3 py-1">
        {found.list.map((c) => (
          <CreatorCard key={c.id} creator={c} page={found.page} />
        ))}
      </HScroll>
    </section>
  );
}

function CreatorCard({ creator, page }: { creator: Creator; page: string }) {
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const el = ref.current;
    const url = creator.impression;
    if (!el || !url) return;
    let timer: number | undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        window.clearTimeout(timer);
        if (!entry || entry.intersectionRatio < SEEN_SHARE) return;
        timer = window.setTimeout(() => {
          if (document.visibilityState !== "visible") return;
          io.disconnect();
          // A plain image request (GET), as OnlyTraffic's own snippet does: the pixel doesn't count a POST.
          new Image().src = url;
        }, SEEN_MS);
      },
      { threshold: SEEN_SHARE },
    );
    io.observe(el);
    return () => {
      window.clearTimeout(timer);
      io.disconnect();
    };
  }, [creator.impression]);

  const href = `/go?${new URLSearchParams({ t: creator.token, ...(creator.fallback && { s: creator.fallback }), p: page })}`;
  const photo = creator.photos.find((p) => p.width >= 400) ?? creator.photos[creator.photos.length - 1];
  const stats = [creator.followers !== null && `${compact(creator.followers)} fans`, creator.likes !== null && `${compact(creator.likes)} likes`]
    .filter(Boolean)
    .join(" · ");

  return (
    // Not "noreferrer": /go reads the page the click came from in the Referer (and doesn't pass it on).
    <a ref={ref} href={href} target="_blank" rel="noopener sponsored" data-stat="creator:row" className="group/creator block w-[136px] shrink-0 sm:w-[156px]">
      <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-surface ring-1 ring-white/[0.04]">
        {/* eslint-disable-next-line @next/next/no-img-element -- photos come straight from OnlyTraffic's CDN */}
        <img
          src={photo.url}
          srcSet={creator.photos.map((p) => `${p.url} ${p.width}w`).join(", ")}
          sizes="156px"
          alt={`@${creator.username}`}
          loading="lazy"
          decoding="async"
          className="size-full object-cover transition-transform duration-300 group-hover/creator:scale-[1.03]"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-2.5 pb-2 pt-10">
          <p className="truncate text-[13px] font-medium text-white">@{creator.username}</p>
          {stats && <p className="truncate text-[11.5px] text-white/70">{stats}</p>}
        </div>
      </div>
      {creator.description && <p className="mt-1.5 line-clamp-2 text-[12px] leading-snug text-muted group-hover/creator:text-soft">{creator.description}</p>}
    </a>
  );
}
